// src/pages/Profile.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import { getProfile, sendVerify } from '../services/userService';
import { toast } from 'react-toastify';

const MIN_LEN = 8;
const hasLower = (s) => /[a-z]/.test(s);
const hasUpper = (s) => /[A-Z]/.test(s);
const hasNumber = (s) => /[0-9]/.test(s);

export default function Profile() {
  const [user, setUser] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // verify banner state
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // change password form
  const [oldPwd, setOldPwd] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [caps, setCaps] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  // hydrate from cache fast, then refresh from API
  useEffect(() => {
    try {
      const cached = localStorage.getItem('me');
      if (cached) setUser(JSON.parse(cached));
    } catch {}
    (async () => {
      setLoadingProfile(true);
      try {
        const fresh = await getProfile();
        setUser(fresh);
        localStorage.setItem('me', JSON.stringify(fresh));
      } catch {
        toast.error('Failed to load profile.');
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, []);

  // cooldown ticker
  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const checks = useMemo(
    () => ({
      len: (pwd || '').length >= MIN_LEN,
      lower: hasLower(pwd),
      upper: hasUpper(pwd),
      num: hasNumber(pwd),
      match: pwd.length > 0 && pwd === pwd2,
    }),
    [pwd, pwd2]
  );

  const canChangePwd =
    !!oldPwd && checks.len && checks.lower && checks.upper && checks.num && checks.match && !savingPwd;

  async function refreshProfile() {
    setRefreshing(true);
    try {
      const fresh = await getProfile();
      setUser(fresh);
      localStorage.setItem('me', JSON.stringify(fresh));
      toast.success('Profile updated.');
    } catch {
      toast.error('Could not refresh profile.');
    } finally {
      setRefreshing(false);
    }
  }

  async function onResendVerify() {
    if (!user) return;
    if (user.emailVerifiedAt) {
      toast.info('Your email is already verified.');
      return;
    }
    if (cooldown > 0) return;

    setResending(true);
    try {
      await sendVerify();
      toast.success('Verification email sent.');
      setCooldown(30); // UI cooldown; server still enforces rate limits
    } catch (err) {
      if (err?._is429) {
        const after = err?._retryAfter ? ` Try again in ~${err._retryAfter}s.` : '';
        toast.warning(`You’re sending verification emails too quickly.${after}`);
        setCooldown(Math.max(30, Number(err?._retryAfter || 0)));
      } else if (err?._auth) {
        toast.error('Session expired. Please sign in again.');
      } else {
        toast.error('Could not resend verification email.');
      }
    } finally {
      setResending(false);
    }
  }

  async function onChangePassword(e) {
    e.preventDefault();
    if (!canChangePwd) return;

    setSavingPwd(true);
    try {
      await api.put('/auth/change-password', {
        oldPassword: oldPwd,
        newPassword: pwd,
        confirmPassword: pwd2,
      });
      toast.success('Password updated.');
      setOldPwd('');
      setPwd('');
      setPwd2('');
    } catch (err) {
      if (err?._status === 400) {
        toast.error(err?._data?.msg || 'Validation error. Please check your inputs.');
      } else if (err?._status === 404) {
        toast.error('User not found.');
      } else if (err?._is429) {
        toast.warning('Too many attempts. Please try again later.');
      } else if (err?._auth) {
        toast.error('Session expired. Please sign in again.');
      } else {
        toast.error('Could not update password. Please try again.');
      }
    } finally {
      setSavingPwd(false);
    }
  }

  if (loadingProfile && !user) {
    return <div className="p-6 text-gray-600">Loading…</div>;
  }
  if (!user) {
    return <div className="p-6 text-red-600">Unable to load profile.</div>;
  }

  const VerifiedBadge = () =>
    user.emailVerifiedAt ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
        Verified
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        Unverified
      </span>
    );

  return (
    <div className="p-6 space-y-6">
      {/* Verify banner */}
      {!user.emailVerifiedAt && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-amber-800">
              <b>Verify your email:</b> We’ve sent a verification link to <b>{user.email}</b>. Didn’t get it?
            </div>
            <button
              type="button"
              onClick={onResendVerify}
              disabled={resending || cooldown > 0}
              className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-60"
            >
              {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}
            </button>
          </div>
        </div>
      )}

      {/* Profile card */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-xl font-semibold">My Profile</h1>
          <button
            onClick={refreshProfile}
            disabled={refreshing}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Name</div>
            <div className="font-medium">{user.name || '-'}</div>
          </div>
          <div>
            <div className="text-gray-500">Email</div>
            <div className="font-medium flex items-center gap-2">
              {user.email}
              <VerifiedBadge />
            </div>
          </div>
          <div>
            <div className="text-gray-500">Role</div>
            <div className="font-medium capitalize">{user.role}</div>
          </div>
          <div>
            <div className="text-gray-500">Last login</div>
            <div className="font-medium">
              {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Change password</h2>
        <form onSubmit={onChangePassword} className="space-y-4">
          <div>
            <label htmlFor="oldPwd" className="block text-sm font-medium text-gray-700">
              Current password
            </label>
            <input
              id="oldPwd"
              type="password"
              className="mt-1 w-full border rounded px-3 py-2 border-gray-300 focus:border-gray-500 outline-none"
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="pwd" className="block text-sm font-medium text-gray-700">
                New password
              </label>
              <input
                id="pwd"
                type="password"
                className="mt-1 w-full border rounded px-3 py-2 border-gray-300 focus:border-gray-500 outline-none"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                onKeyUp={(e) => setCaps(e.getModifierState && e.getModifierState('CapsLock'))}
                autoComplete="new-password"
                required
              />
              {caps && <p className="mt-1 text-xs text-amber-600">Caps Lock is on.</p>}
            </div>
            <div>
              <label htmlFor="pwd2" className="block text-sm font-medium text-gray-700">
                Confirm new password
              </label>
              <input
                id="pwd2"
                type="password"
                className={`mt-1 w-full border rounded px-3 py-2 outline-none ${
                  pwd2.length > 0 && !checks.match ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
                }`}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                onKeyUp={(e) => setCaps(e.getModifierState && e.getModifierState('CapsLock'))}
                autoComplete="new-password"
                required
              />
              {pwd2.length > 0 && !checks.match && (
                <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
              )}
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <p className="text-xs font-medium text-gray-700 mb-2">Password must include:</p>
            <ul className="grid grid-cols-2 gap-y-1 text-sm">
              <Requirement ok={checks.len} label={`At least ${MIN_LEN} characters`} />
              <Requirement ok={checks.upper} label="An uppercase letter" />
              <Requirement ok={checks.lower} label="A lowercase letter" />
              <Requirement ok={checks.num} label="A number" />
            </ul>
          </div>

          <button
            type="submit"
            disabled={!canChangePwd}
            className="px-4 py-2 rounded bg-black text-white disabled:opacity-60"
          >
            {savingPwd ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Requirement({ ok, label }) {
  return (
    <li className={`text-sm ${ok ? 'text-green-600' : 'text-gray-500'}`}>
      <span className={`inline-block w-4 text-center mr-2 ${ok ? '' : 'opacity-60'}`}>{ok ? '✓' : '•'}</span>
      {label}
    </li>
  );
}
